import * as React from "react";
import { Treemap, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";
import { CHART_COLORS } from "@/lib/chart-colors";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart";
import type { PerformanceData, Asset, StockAsset, EtfAsset } from "@/schemas";
import { useBaseCurrency } from "@/hooks/use-base-currency";

interface IndustryAllocationChartProps {
  performanceData: PerformanceData | null;
  assetsMap: Map<string, Asset>;
  loading?: boolean;
  className?: string;
}

export function IndustryAllocationChart({
  performanceData,
  assetsMap,
  loading = false,
  className,
}: IndustryAllocationChartProps) {
  const { code: currencyCode } = useBaseCurrency();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatCompactCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  };

  const { chartData, chartConfig, totalValue, industryCount } =
    React.useMemo(() => {
      if (!performanceData) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          industryCount: 0,
        };
      }

      const assets = performanceData.assets_performance;
      const totalPerf = performanceData.total_performance;

      if (!assets || Object.keys(assets).length === 0) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          industryCount: 0,
        };
      }

      if (!totalPerf) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          industryCount: 0,
        };
      }

      // If assets are still loading, return empty data
      if (assetsMap.size === 0) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          industryCount: 0,
        };
      }

      // Group by industry and calculate total for stocks/ETFs only
      const industryGroups: Record<string, number> = {};
      let industryTotal = 0;

      Object.entries(assets).forEach(([assetId, assetPerf]) => {
        const asset = assetsMap.get(assetId);
        // Only include stocks and ETFs which have industry information
        if (asset && (asset.type === "stock" || asset.type === "etf")) {
          const industry =
            (asset as StockAsset | EtfAsset).industry || "Unknown";
          const value = assetPerf.current_value || 0;
          industryGroups[industry] = (industryGroups[industry] || 0) + value;
          industryTotal += value;
        }
      });

      if (industryTotal <= 0) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          industryCount: 0,
        };
      }

      const finalTotal = industryTotal;

      const children: Array<{
        name: string;
        size: number;
        percentage: number;
        color: string;
        key: string;
      }> = [];
      const config: ChartConfig = {
        value: { label: "Value" },
      };

      Object.entries(industryGroups)
        .filter(([, value]) => value > 0)
        .sort(([, a], [, b]) => b - a)
        .forEach(([industry, value], index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length];
          children.push({
            name: industry,
            size: value,
            percentage: (value / finalTotal) * 100,
            color,
            key: industry,
          });
          config[industry] = {
            label: industry,
            color,
          };
        });

      return {
        chartData: children,
        chartConfig: config,
        totalValue: finalTotal,
        industryCount: children.length,
      };
    }, [performanceData, assetsMap]);

  if (loading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center h-full w-full text-muted-foreground text-sm",
          className,
        )}
      >
        Loading...
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div
        className={cn(
          "flex items-center justify-center h-full w-full text-muted-foreground text-sm",
          className,
        )}
      >
        No industry data
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full w-full", className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <span className="text-sm font-medium text-foreground">
          Industry Allocation
        </span>
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-muted-foreground">
            {industryCount} industries
          </span>
          <span className="text-sm font-semibold tracking-tight">
            {formatCompactCurrency(totalValue)}
          </span>
        </div>
      </div>

      {/* Content: Treemap */}
      <div className="flex-1 min-h-0 w-full">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={chartData}
              dataKey="size"
              stroke="hsl(var(--background))"
              fill="hsl(var(--primary))"
              content={<CustomTreemapContent />}
              isAnimationActive={false}
            >
              <ChartTooltip
                content={<AllocationTooltip formatCurrency={formatCurrency} />}
              />
            </Treemap>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
    </div>
  );
}

type TreemapContentProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  color?: string;
  percentage?: number;
  payload?: { percentage?: number };
};

const CustomTreemapContent = (props: TreemapContentProps) => {
  const { x = 0, y = 0, width = 0, height = 0, name = "", color = "" } = props;
  const percentage = props.percentage ?? props.payload?.percentage ?? 0;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: "hsl(var(--background))",
          strokeWidth: 2,
        }}
      />
      {width > 30 && height > 30 && (
        <foreignObject x={x} y={y} width={width} height={height}>
          <div className="w-full h-full p-1 flex flex-col items-center justify-center text-center gap-1 overflow-hidden">
            <span
              className="text-xs font-semibold text-white leading-tight line-clamp-2"
              title={name}
            >
              {name}
            </span>
            {height > 45 && (
              <span className="text-xs text-white/90 font-mono font-semibold">
                {percentage.toFixed(0)}%
              </span>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  );
};

const AllocationTooltip = ({
  active,
  payload,
  formatCurrency,
}: {
  active?: boolean;
  payload?: Array<{
    payload?: { name?: string; size?: number; percentage?: number };
    name?: string;
    value?: number;
  }>;
  formatCurrency: (value: number) => string;
}) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0]?.payload;
  if (!entry) return null;

  const name = entry.name ?? payload[0]?.name;
  const value = typeof entry.size === "number" ? entry.size : payload[0]?.value;
  const percentage =
    typeof entry.percentage === "number" ? entry.percentage : 0;

  return (
    <div className="rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-sm shadow-xl">
      <div className="flex flex-col gap-1 min-w-[180px]">
        <span className="text-sm font-semibold">{name}</span>
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-sm">
            {formatCurrency(value as number)}
          </span>
          <span className="text-muted-foreground text-sm">
            {percentage.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};
